/**
 * Delivering the submission outputs (Feature #5615, stories 5630/5632/5637).
 *
 * The content of both outputs is built server-side from the stored onboarding; these helpers only
 * put it where the user needs it — a file on disk, or the clipboard. A clipboard failure is always
 * reported back rather than swallowed (stories 5631/5636).
 */

import moment from "moment";

/** Writes the JSON envelope to a file with the deterministic filename the server chose (S7.14). */
export const downloadJsonFile = (fileName: string, json: string): void => {
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export type CopyResult = { ok: boolean; message: string };

/**
 * Copies one raw value. Falls back to the legacy path when the async clipboard is unavailable (an
 * insecure origin, or an older browser), and reports failure rather than pretending it worked.
 */
export const copyToClipboard = async (
  value: string,
  what: string = "Value",
): Promise<CopyResult> => {
  const text = `${value ?? ""}`;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return { ok: true, message: `${what} copied` };
    }
  } catch {
    // Fall through to the legacy path before giving up.
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    if (copied) return { ok: true, message: `${what} copied` };
  } catch {
    // Reported below.
  }

  return {
    ok: false,
    message: `${what} could not be copied — select the text and copy it by hand.`,
  };
};

/** Format a date/time to local device timezone: e.g. "1 September 2026, 12:04 am NZST" */
export const formatLocalDeviceDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return "—";
  const m = moment.utc(value).local();
  if (!m.isValid()) return "—";
  const dateStr = m.format("D MMMM YYYY, h:mm a");
  
  let tzAbbr = "";
  try {
    const parts = new Intl.DateTimeFormat("en-NZ", { timeZoneName: "short" })
      .formatToParts(m.toDate());
    const tzPart = parts.find((part) => part.type === "timeZoneName");
    if (tzPart) {
      tzAbbr = tzPart.value;
      // Do not display raw offset zones (GMT+12, UTC, etc.)
      if (
        tzAbbr.includes("GMT") ||
        tzAbbr.includes("UTC") ||
        tzAbbr.includes("+") ||
        tzAbbr.includes("-")
      ) {
        tzAbbr = "";
      }
    }
  } catch (e) {
    // fallback to timezone or blank
  }
  
  return tzAbbr ? `${dateStr} ${tzAbbr}` : dateStr;
};

/** Format a date only to local device timezone: e.g. "1 September 2026" */
export const formatLocalDeviceDate = (value: string | Date | null | undefined): string => {
  if (!value) return "—";
  const m = moment.utc(value).local();
  if (!m.isValid()) return "—";
  return m.format("D MMMM YYYY");
};

/** Scans a string and replaces [[UTC:<ISO-String>:datetime]] and [[UTC:<ISO-String>:date]] tokens with local time */
export const replaceDateTimePlaceholders = (text: string | null | undefined): string => {
  if (!text) return "";
  let result = text;
  result = result.replace(/\[\[UTC:(.*?):datetime\]\]/g, (match, isoString) => {
    return formatLocalDeviceDateTime(isoString);
  });
  result = result.replace(/\[\[UTC:(.*?):date\]\]/g, (match, isoString) => {
    return formatLocalDeviceDate(isoString);
  });
  return result;
};

/** en-NZ, matching the stamps the SOP prints (S7.11) - now timezone aware. */
export const formatOutputDateTime = (value: string | null | undefined): string => {
  return formatLocalDeviceDateTime(value);
};
